import { ErrorHandler, Injectable, Injector, NgZone } from '@angular/core';
import { UserAuthService } from './user-auth/user-auth.service';

/**
 * Application-wide safety net for otherwise-unhandled runtime errors.
 *
 * The goal is to make sure the user is never left stuck on an unresponsive
 * blank screen when a critical token, key or cached configuration value is
 * invalid, expired, missing, corrupted or outdated. Instead of letting the
 * error bubble up and tear down the view, we recover gracefully:
 *
 *  - ChunkLoadError (a lazy-loaded bundle failed to download — almost always a
 *    stale build after a new deployment): force a hard reload so the browser
 *    fetches the fresh chunks.
 *  - MSAL / session-cache corruption (the auth cache in storage no longer maps
 *    to a usable account/token): clear everything, log the user out and send
 *    them back to the login page.
 *  - Anything else: log it for diagnostics without swallowing it silently.
 */
@Injectable()
export class GlobalErrorHandler implements ErrorHandler {
  // Signals in an error's name/message that indicate the persisted MSAL auth
  // cache (or other critical session state) is no longer usable and the only
  // safe recovery is a clean logout + redirect to login.
  private readonly sessionCorruptionSignals = [
    'BrowserAuthError',
    'ClientAuthError',
    'InteractionRequiredAuthError',
    'no_account_error',
    'no_tokens_found',
    'monitor_window_timeout',
    'endpoints_resolution_error'
  ];

  constructor(private injector: Injector, private zone: NgZone) {}

  handleError(error: any): void {
    // Angular wraps promise rejections; unwrap to inspect the real cause.
    const cause = error?.rejection || error?.originalError || error;
    const message = this.extractMessage(cause);
    const name = (cause && cause.name) || '';

    // 1. A lazy-loaded chunk failed to load — recover with a fresh reload.
    if (this.isChunkLoadError(name, message)) {
      console.error('Chunk load failed - reloading to fetch fresh build:', message);
      this.recoverFromChunkError();
      return;
    }

    // 2. The persisted auth/session cache is corrupted or no longer valid —
    //    clear storage, log out and return the user to the login page.
    if (this.isSessionCorruptionError(name, message)) {
      console.error('Session/auth cache is invalid - logging out:', name || message);
      this.recoverBySigningOut();
      return;
    }

    // 3. Everything else: keep the app alive and surface the error in the console.
    console.error('Unhandled application error:', error);
  }

  private extractMessage(cause: any): string {
    if (!cause) { return ''; }
    if (typeof cause === 'string') { return cause; }
    return cause.message || cause.toString?.() || '';
  }

  private isChunkLoadError(name: string, message: string): boolean {
    return name === 'ChunkLoadError' ||
      /Loading chunk [\w-]+ failed/i.test(message) ||
      /Loading CSS chunk/i.test(message);
  }

  private isSessionCorruptionError(name: string, message: string): boolean {
    return this.sessionCorruptionSignals.some(
      signal => name.includes(signal) || message.includes(signal)
    );
  }

  private recoverFromChunkError(): void {
    // Guard against an infinite reload loop if the chunk is genuinely gone:
    // only auto-reload if we have not just done so.
    const lastReload = Number(sessionStorage.getItem('lastChunkReload') || 0);
    if (Date.now() - lastReload < 10000) {
      return;
    }
    sessionStorage.setItem('lastChunkReload', String(Date.now()));
    this.zone.run(() => window.location.reload());
  }

  private recoverBySigningOut(): void {
    // Resolve lazily to avoid a construction-time circular dependency, since the
    // ErrorHandler is instantiated very early during bootstrap.
    const userAuthService = this.injector.get(UserAuthService);
    this.zone.run(() => userAuthService.logOut());
  }
}
